'use client';

import {
  ComponentProps,
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useClickOutside } from 'hooks/dom/useClickOutside';
import { useCartStore, useShallow } from '..';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { useModal } from 'components/shared/Modal';
import { useTranslations } from 'next-intl';
import { DotSpinner } from 'components/shared';
import { PlusCircleDark } from 'assets/icons/PlusCircleDark';
import { CrossBVModal } from 'assets/icons/CrossBVModal';
import { InfoIcon } from 'assets/icons';
import { Currency } from 'utils/Currency';
import { useFormatPrice, getCurrencySymbolFromEnum } from 'hooks/pricing/useFormattedPrice';
import { ProductType } from 'components/shared/Cart/types';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  BvUpsellCard,
  getUsBvVariantSetForVariantId,
} from 'content/products/bloodVisionUpsell';
import {
  getAeCitySuggestionPopoverLayout,
  getBvLearnMoreLinkLabel,
  getUsBvMergedDisplay,
  getUsBvSharedCardSnapshot,
  isBvCardInCart,
} from 'utils/bloodVisionUpsell';
import {
  computeBvDisplayPricePersistentSheet,
  formatBvAtcPriceLabel,
  getBvCanAddToCartEffective,
  getBvDisableAddToCart,
  shouldUseBvUsSharedSnapshot,
  getBvHasResolvedBackendPriceCombined,
  getBvShopifyVariantIdForAtc,
  getBvShowStartingAtRow,
} from 'utils/bvUpsellPresentation';
import { useBvPincodeValidator } from 'hooks/blood-vision/useBvPincodeValidator';
import { useBvUsZipPlansSnapshot } from 'hooks/blood-vision/useBvUsZipPlansSnapshot';
import BloodVisionUpsellModal from 'components/blood-vision/BloodVisionUpsellModal';
import { BvUpsellPlanImage } from 'components/blood-vision/BvUpsellPlanImage';
import {
  ShopProductCardRoot,
  ShopCardBody,
  ShopCardBottomSheet,
  ShopCardFooterRow,
  ShopCardMediaDimBackdrop,
  ShopCardPlanTileMedia,
  ShopCardPrimaryCta,
  ShopCardSpecSection,
  ShopCardTag,
  ShopCardTagText,
} from './shopProductCard';
import styles from './ProductCardBloodVision.module.css';

const BvMedia = ({
  className,
  ...props
}: ComponentProps<typeof ShopCardPlanTileMedia>) => (
  <ShopCardPlanTileMedia
    className={[styles.bvMedia, className].filter(Boolean).join(' ')}
    {...props}
  />
);

interface ProductCardBloodVisionProps {
  className?: string;
  card: BvUpsellCard;
  activeOverlayCardId: string | null;
  setActiveOverlayCardId: (id: string | null) => void;
}

export const ProductCardBloodVision = ({
  className,
  card,
  activeOverlayCardId,
  setActiveOverlayCardId,
}: ProductCardBloodVisionProps) => {
    const modalClient = useModal();
    const cartClient = useCartStore(
      useShallow((s) => ({
        store: s.store,
        currency: s.currency,
        cart: s.cart,
        taxString: s.taxString,
        addToCart: s.addToCart,
      })),
    );
    const { store: regionStore } = useRegionLocale();
    const tCart = useTranslations('cart');
    const t = useTranslations('ring');
    const store = (cartClient.store ?? regionStore) as ShopifyStore | null;

    const showInnerOverlay = activeOverlayCardId === card.id;
    const [isAdding, setIsAdding] = useState(false);
    const [isAddedToCartLocal, setIsAddedToCartLocal] = useState(false);
    const [showUsPricingInfo, setShowUsPricingInfo] = useState(false);
    const [aeCityPopoverStyle, setAeCityPopoverStyle] =
      useState<CSSProperties | null>(null);
    const [aeCityPopoverPlacement, setAeCityPopoverPlacement] = useState<
      'above' | 'below'
    >('below');
    const sheetRef = useRef<HTMLDivElement>(null);
    const aeCityListRef = useRef<HTMLUListElement | null>(null);

    const closeInnerOverlay = useCallback(() => {
      setActiveOverlayCardId(null);
      setShowUsPricingInfo(false);
    }, [setActiveOverlayCardId]);

    const hoverOutSheet = useCallback(() => {
      if (showInnerOverlay) {
        setTimeout(() => {
          closeInnerOverlay();
        }, 200);
      }
    }, [showInnerOverlay, closeInnerOverlay]);

    const zipPlans = useBvUsZipPlansSnapshot();
    const sessionSnapForCart = useMemo(
      () =>
        getUsBvSharedCardSnapshot(
          card,
          zipPlans.committedZip,
          zipPlans.shopifyPlansResponse,
        ),
      [
        card,
        zipPlans.committedZip,
        zipPlans.revision,
        zipPlans.shopifyPlansResponse,
      ],
    );

    const {
      pincode,
      onPincodeChange,
      inputRef,
      inputConfig,
      statusText,
      statusKind,
      canAddToCart,
      isCheckingPincode,
      resolvedPrice,
      hasResolvedBackendPrice,
      resolvedUsPlanDisplay,
      resolvedShopifyVariantId,
      setStatusMessage,
      usBvSharedCardSnapshot: sharedSnap,
      usCommittedZip,
      isAeCityMode,
      aeCitySuggestions,
      showAeCitySuggestions,
      onAeCitySuggestionSelect,
      onAeCityInputFocus,
      closeAeCitySuggestions,
    } = useBvPincodeValidator({
      card,
      store,
      active: showInnerOverlay && !isAddedToCartLocal,
    });

    useClickOutside(
      sheetRef,
      hoverOutSheet,
      showInnerOverlay && !showUsPricingInfo && !showAeCitySuggestions,
    );

    useEffect(() => {
      if (!isAeCityMode || !showAeCitySuggestions || !inputRef.current) {
        setAeCityPopoverStyle(null);
        return;
      }

      const updatePosition = () => {
        const rect = inputRef.current?.getBoundingClientRect();
        if (!rect) return;
        const { placement, style } = getAeCitySuggestionPopoverLayout(
          rect,
          aeCitySuggestions.length,
        );
        setAeCityPopoverPlacement(placement);
        setAeCityPopoverStyle(style);
      };

      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }, [
      isAeCityMode,
      showAeCitySuggestions,
      inputRef,
      aeCitySuggestions.length,
    ]);

    useEffect(() => {
      if (!isAeCityMode || !showAeCitySuggestions) return;
      const onPointerDown = (event: MouseEvent) => {
        const target = event.target as Node | null;
        if (!target) return;
        if (inputRef.current?.contains(target)) return;
        if (aeCityListRef.current?.contains(target)) return;
        closeAeCitySuggestions();
      };
      document.addEventListener('mousedown', onPointerDown);
      return () => document.removeEventListener('mousedown', onPointerDown);
    }, [isAeCityMode, showAeCitySuggestions, closeAeCitySuggestions, inputRef]);

    const useSharedSnapState =
      !!sharedSnap &&
      shouldUseBvUsSharedSnapshot(store, pincode, usCommittedZip, {
        sheetOpen: showInnerOverlay,
      });
    const effectiveUsState = useSharedSnapState
      ? {
        resolvedUsPlanDisplay: sharedSnap.resolvedUsPlanDisplay,
        hasResolvedBackendPrice: false,
        resolvedPrice: undefined as number | undefined,
        canAddToCart: !!sharedSnap.isServiceable,
        resolvedShopifyVariantId: sharedSnap.resolvedShopifyVariantId,
      }
      : {
        resolvedUsPlanDisplay,
        hasResolvedBackendPrice,
        resolvedPrice,
        canAddToCart,
        resolvedShopifyVariantId,
      };

    const displayFields = useMemo(
      () =>
        getUsBvMergedDisplay(
          store,
          card,
          effectiveUsState.resolvedUsPlanDisplay,
        ),
      [store, card, effectiveUsState.resolvedUsPlanDisplay],
    );

    const currency = (cartClient.currency ?? Currency.usd) as Currency;
    const currencySymbol = getCurrencySymbolFromEnum(currency);
    const displayPrice = useMemo(
      () =>
        computeBvDisplayPricePersistentSheet(
          effectiveUsState.hasResolvedBackendPrice,
          effectiveUsState.resolvedPrice,
          sharedSnap,
          card.fallbackPrice,
        ),
      [
        effectiveUsState.hasResolvedBackendPrice,
        effectiveUsState.resolvedPrice,
        card.fallbackPrice,
        sharedSnap?.isServiceable,
        sharedSnap?.resolvedPrice,
      ],
    );
    const formatPriceWithComma = useFormatPrice();
    const displayPriceLabel = displayPrice
      ? `${currencySymbol}${formatPriceWithComma(displayPrice)}`
      : null;
    const isUSStore = store === ShopifyStore.US;
    const learnMoreLinkLabel = getBvLearnMoreLinkLabel(store);
    const hasResolvedBackendPriceCombined =
      getBvHasResolvedBackendPriceCombined(
        store,
        effectiveUsState.hasResolvedBackendPrice,
        sharedSnap,
        { kind: 'collapsible_card', sheetOpen: showInnerOverlay },
      );
    const showStartingAt = getBvShowStartingAtRow(
      isUSStore,
      displayPrice,
      displayPriceLabel,
    );
    const atcPriceLabel = formatBvAtcPriceLabel(
      isUSStore,
      hasResolvedBackendPriceCombined,
      displayPriceLabel,
    );
    const canAddToCartEffective = getBvCanAddToCartEffective(
      effectiveUsState.canAddToCart,
    );

    const shopifyVariantIdForAtc = useMemo(
      () =>
        getBvShopifyVariantIdForAtc(
          store,
          card,
          effectiveUsState.resolvedShopifyVariantId,
          sharedSnap ?? sessionSnapForCart,
        ),
      [
        store,
        card,
        effectiveUsState.resolvedShopifyVariantId,
        sharedSnap?.resolvedShopifyVariantId,
        sessionSnapForCart?.resolvedShopifyVariantId,
      ],
    );
    const isVariantInCart = useMemo(
      () =>
        isBvCardInCart(card, cartClient.cart, store, shopifyVariantIdForAtc),
      [card, cartClient.cart, store, shopifyVariantIdForAtc],
    );
    useEffect(() => {
      if (!isVariantInCart) {
        setIsAddedToCartLocal(false);
      }
    }, [isVariantInCart]);
    const isAddedToCart = isAddedToCartLocal || isVariantInCart;
    const disableAddToCart = getBvDisableAddToCart({
      store,
      isBlockedBecauseInCart: isAddedToCart,
      canAddToCartEffective,
      isCheckingPincode,
      isAdding,
    });
    const usExclusiveRegion = useMemo(
      () =>
        store === ShopifyStore.US && shopifyVariantIdForAtc
          ? getUsBvVariantSetForVariantId(shopifyVariantIdForAtc)
          : null,
      [store, shopifyVariantIdForAtc],
    );

    const openLearnMoreModal = () => {
      modalClient.set(
        <BloodVisionUpsellModal
          card={card}
          onClose={() => modalClient.clear()}
        />,
      );
      modalClient.setFullView();
      modalClient.setCloseBtnHidden(true);
      modalClient.setTop();
      modalClient.setAlignRight();
      modalClient.show();
    };

    const handleAddToCart = async () => {
      if (isAddedToCart) return;
      if (!canAddToCartEffective) return;
      if (!shopifyVariantIdForAtc) {
        setStatusMessage(
          tCart('sharedCart.upsell.notAvailableForCheckout'),
          'error',
        );
        return;
      }
      if (isVariantInCart) {
        setIsAddedToCartLocal(true);
        setStatusMessage(tCart('sharedCart.upsell.itemAlreadyInCart'), 'warning');
        closeInnerOverlay();
        return;
      }

      setIsAdding(true);
      try {
        await cartClient.addToCart([
          {
            shopifyVariantId: shopifyVariantIdForAtc,
            productType: ProductType.GENERIC,
            params: {
              productType: 'BLOOD_VISION',
              maxQuantity: 1,
              noteAttributes: [
                { key: '_funnel', value: 'combined_order' },
                ...(usExclusiveRegion
                  ? [{ key: '_exc_region', value: usExclusiveRegion }]
                  : []),
              ],
            },
          },
        ]);
        setIsAddedToCartLocal(true);
        closeInnerOverlay();
      } catch (error) {
        console.error('BV add to cart failed from shop card', error);
        setStatusMessage(tCart('sharedCart.upsell.unableToAddPlan'), 'error');
      } finally {
        setIsAdding(false);
      }
    };

    const innerOverlayId = `bv-shop-overlay-${card.id}`;
    const pricingNoteId = `bv-shop-pricing-note-${card.id}`;
    const aeCityListboxId = `bv-shop-ae-cities-${card.id}`;

    return (
      <ShopProductCardRoot
        as="article"
        className={[styles.root, className].filter(Boolean).join(' ')}
        aria-label={displayFields.title}
      >
        <BvMedia
          onClick={() => {
            if (showInnerOverlay) {
              closeInnerOverlay();
            } else {
              openLearnMoreModal();
            }
          }}
          aria-label={`${learnMoreLinkLabel}: ${displayFields.title}`}
        >
          <BvUpsellPlanImage
            src={displayFields.imageSrc}
            alt={displayFields.title}
            height={335}
            width={355}
            style={{ objectFit: 'contain', width: '100%', borderRadius: '12px' }}
          />
        </BvMedia>

        <ShopCardBody>
          <ShopCardSpecSection className={`specBlock ${styles.specBlock}`}>
            <h3 className="title">{displayFields.title}</h3>
            <p className="description">
              {displayFields.description}{' '}
              <button
                className="learnMoreLink"
                onClick={openLearnMoreModal}
                data-buttontype={
                  isUSStore
                    ? 'check price in your region'
                    : 'check serviceability'
                }
                data-product={displayFields.title}
              >
                {learnMoreLinkLabel}{' '}
                <PlusCircleDark
                  fill="#007FF5"
                  style={{
                    width: '12px',
                    height: '12px',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    marginLeft: '2px',
                  }}
                />
              </button>
            </p>
          </ShopCardSpecSection>
          <ShopCardFooterRow className={`pricing ${styles.pricing}`}>
            <div className="pricing-info">
              <div className={`priceBlock ${isUSStore ? 'usPriceBlock' : ''}`}>
                {showStartingAt ? (
                  <span className="startingAtLabel">
                    {tCart('sharedCart.upsell.startsFrom')}
                  </span>
                ) : null}
                {displayPriceLabel ? (
                  <div className="priceRow">
                    <span className="priceValue">{displayPriceLabel}</span>
                    <span className="taxLabel">{cartClient.taxString}</span>
                  </div>
                ) : (
                  tCart('sharedCart.upsell.priceAtCheckout')
                )}
              </div>
            </div>
            <ShopCardPrimaryCta
              className={isAddedToCart ? 'quickAdd disable' : 'quickAdd'}
              onClick={() => {
                if (!isAddedToCart) setActiveOverlayCardId(card.id);
              }}
              disabled={isAddedToCart}
              data-buttontype="add to cart"
              data-product={card.title}
              aria-haspopup="dialog"
              aria-expanded={showInnerOverlay}
              aria-controls={innerOverlayId}
            >
              {isAddedToCart
                ? tCart('sharedCart.upsell.addedToCart')
                : tCart('sharedCart.upsell.addToCart')}
            </ShopCardPrimaryCta>
          </ShopCardFooterRow>
        </ShopCardBody>

        {card.tag ? <ShopCardTag><ShopCardTagText tag={card.tag} /></ShopCardTag> : null}

        <ShopCardMediaDimBackdrop className={showInnerOverlay ? 'open' : ''} />

        <ShopCardBottomSheet
          id={innerOverlayId}
          ref={sheetRef}
          className={`sizeOverlay ${showInnerOverlay ? 'open' : ''} ${styles.bottomSheet}`}
          role="dialog"
          aria-hidden={!showInnerOverlay}
          aria-label={inputConfig.checkSectionTitle}
        >
          <div className="stepContent">
            {isAddedToCart ? (
              <p className="addedToCartLabel" role="status">
                {tCart('sharedCart.upsell.addedToCart')}
              </p>
            ) : (
              <>
                <div className="innerTitleRow">
                  <div className="innerTitle">
                    {inputConfig.checkSectionTitle}
                  </div>
                  {store === ShopifyStore.US ? (
                    <button
                      type="button"
                      className="pricingInfoTrigger"
                      onClick={() => setShowUsPricingInfo(true)}
                      aria-label={t('ring.shop.openZipPricing')}
                      aria-expanded={showUsPricingInfo}
                      aria-controls={pricingNoteId}
                    >
                      <InfoIcon fill="#000000" />
                    </button>
                  ) : null}
                </div>
                <div className="inputRow">
                  <div className="inputShell">
                    <input
                      ref={inputRef}
                      value={pincode}
                      onChange={(event) => onPincodeChange(event.target.value)}
                      onFocus={() => {
                        if (isAeCityMode) onAeCityInputFocus();
                      }}
                      inputMode={isAeCityMode ? 'text' : 'numeric'}
                      maxLength={inputConfig.inputMaxLength}
                      placeholder={inputConfig.inputPlaceholder}
                      role={isAeCityMode ? 'combobox' : undefined}
                      aria-autocomplete={isAeCityMode ? 'list' : undefined}
                      aria-expanded={
                        isAeCityMode
                          ? Boolean(showAeCitySuggestions)
                          : undefined
                      }
                      aria-controls={isAeCityMode ? aeCityListboxId : undefined}
                      aria-label={tCart('sharedCart.upsell.enterField', {
                        label: inputConfig.inputLabel,
                      })}
                      style={
                        isAeCityMode && showAeCitySuggestions
                          ? {
                            ...(aeCityPopoverPlacement === 'above'
                              ? {
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0,
                              }
                              : {
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 0,
                              }),
                          }
                          : undefined
                      }
                    />
                    <span className="inputLoader" aria-hidden>
                      {isCheckingPincode ? (
                        <DotSpinner color="#057ff0" />
                      ) : null}
                    </span>
                  </div>
                </div>

                <p
                  className={`statusText ${statusKind}`}
                  role="status"
                  aria-live="polite"
                >
                  {statusText}
                </p>

                <button
                  type="button"
                  className="addToCart"
                  disabled={disableAddToCart}
                  onClick={handleAddToCart}
                  data-buttontype="add to cart"
                  data-product={displayFields.title}
                  aria-busy={isAdding}
                >
                  {isAdding ? <DotSpinner color="#FFFFFF" /> : atcPriceLabel}
                </button>
              </>
            )}
          </div>
        </ShopCardBottomSheet>
        {!isAddedToCart &&
          isAeCityMode &&
          showAeCitySuggestions &&
          aeCityPopoverStyle &&
          typeof document !== 'undefined'
          ? createPortal(
            <ul
              id={aeCityListboxId}
              ref={aeCityListRef}
              role="listbox"
              aria-label={tCart('sharedCart.upsell.availableCities')}
              style={{
                ...aeCityPopoverStyle,
                margin: 0,
                padding: 6,
                listStyle: 'none',
                borderRadius: 10,
                border: '1px solid rgba(0, 0, 0, 0.12)',
                background: '#ffffff',
                maxHeight: 220,
                overflowY: 'auto',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                ...(aeCityPopoverPlacement === 'above'
                  ? {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  }
                  : {
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                  }),
              }}
            >
              {aeCitySuggestions.map((city) => (
                <li
                  key={city.id}
                  style={
                    city === aeCitySuggestions[aeCitySuggestions.length - 1]
                      ? undefined
                      : { borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }
                  }
                >
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onAeCitySuggestionSelect(city.name)}
                    style={{
                      width: '100%',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      padding: '8px 10px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      color: '#000000',
                      fontSize: 14,
                    }}
                  >
                    {city.name}
                  </button>
                </li>
              ))}
            </ul>,
            document.body,
          )
          : null}
        {showUsPricingInfo ? (
          <section
            id={pricingNoteId}
            className="pricingInfoModal"
            aria-label={t('ring.shop.pricing.usAria')}
          >
            <button
              type="button"
              className="pricingInfoClose"
              onClick={() => setShowUsPricingInfo(false)}
              aria-label={t('ring.shop.pricing.close')}
            >
              <CrossBVModal />
            </button>
            <h3>{t('ring.shop.pricing.varyByState')}</h3>
            <p>{t('ring.shop.pricing.varyByStateBody')}</p>
          </section>
        ) : null}
      </ShopProductCardRoot>
    );
};
